import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'

// Light and dark, the main app's way (src/app/components/ThemeProvider.tsx): a
// `.dark` class on <html>, the choice kept under the same localStorage key,
// and the system setting until someone chooses. index.html applies it before
// first paint. The key is shared by name only for now: Writings runs on its
// own origin, so the two remember separately until it moves into the app.

export type Theme = 'light' | 'dark'

export const THEME_KEY = 'burrow-theme'

/** The browser chrome's colour: the app's --background in each theme. */
const THEME_COLOR: Record<Theme, string> = { light: '#F3EAE0', dark: '#1A1210' }

function initialTheme(): Theme {
  try {
    const stored = window.localStorage.getItem(THEME_KEY)
    if (stored === 'light' || stored === 'dark') return stored
    return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  } catch {
    return 'light'
  }
}

const ThemeContext = createContext<{ theme: Theme; toggleTheme: () => void }>({
  theme: 'light',
  toggleTheme: () => {},
})

export function useTheme() {
  return useContext(ThemeContext)
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(initialTheme)

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark')
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', THEME_COLOR[theme])
    try {
      window.localStorage.setItem(THEME_KEY, theme)
    } catch {
      /* storage unavailable: the choice lasts for this visit */
    }
  }, [theme])

  const toggleTheme = useCallback(() => setTheme((t) => (t === 'light' ? 'dark' : 'light')), [])

  return <ThemeContext.Provider value={{ theme, toggleTheme }}>{children}</ThemeContext.Provider>
}
